import AppContext from "PFApp/app_context";
import AutoSelect from "PFComponents/select/autoselect";
import EchartsWrapper from "PFCore/helpers/echarts_wrapper";
import useForceUpdate from "PFCore/helpers/use_force_update";
import useInsightsApi from "PFCore/helpers/use_insights_api";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import PiePreview from "../../previews/pie_preview";
import { pieChartConfig } from "./configs/pie_chart_config";
import css from "./roles_pie_chart.module.scss";

const RolesPieChart = () => {
  const {
    store: {
      pages: {
        insights: { selectedFilters, setModalData }
      }
    }
  } = useContext(AppContext);

  const [graphData, setGraphData] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [dimension, setDimension] = useState({ value: "workflow_state", text: "Status" });
  const [dimensionValues, setDimensionValues] = useState([]);
  const dimensionValuesKey = useMemo(
    () => graphData && graphData.values && graphData.values.map((item) => item.label).join(""),
    [graphData]
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { insightsApi, insightsApiError } = useInsightsApi();
  const { t } = useTranslation("insights");
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    setLoading(true);
    insightsApi("/roles_pie", {
      dim: dimension.value,
      value: dimensionValues.map((value) => value.value).join(","),
      ...selectedFilters
    }).then((res) => {
      setGraphData(res);
      setLoading(false);
    });
  }, [selectedFilters, dimension, dimensionValues]);

  const displayValues = useMemo(() => {
    if (dropdownOpen) {
      return <span className={dimensionValues.length > 0 ? css.value : null}>&nbsp;</span>;
    } else if (dimensionValues.length === 1) {
      return (
        <span className={css.value}>
          <span>{dimensionValues[0].text}</span>
        </span>
      );
    } else if (dimensionValues.length > 1) {
      return (
        <span className={css.value}>
          <span>{t("filters.manyValuesSelected", { count: dimensionValues.length })}</span>
        </span>
      );
    } else {
      // TODO: [ENG-84] come back and remove select... placeholder?
      return <span className={css.fakePlaceholder}>{t("filters.selectPlaceholder")}</span>;
    }
  }, [dropdownOpen, dimensionValues]);

  if (insightsApiError) {
    return <PiePreview content={t("noData")} />;
  }

  const displayContent = Boolean(graphData);

  if (!displayContent) {
    return <PiePreview animated content={t("loading")} />;
  }

  return (
    <>
      <div className={css.rolesPieSection}>
        <h3>{t("rolesPie")}</h3>
        <div className={css.rolesPieSectionFilters}>
          <AutoSelect
            multi={false}
            closeOnChange={true}
            values={[dimension]}
            letClear={false}
            query={(term) =>
              Promise.resolve(
                graphData.dims
                  .map((item) => ({ text: item.label, value: item.value }))
                  .filter((item) => item.value.includes(term))
              )
            }
            handleChange={(dimension) => {
              setDimension(dimension[0]);
              setDimensionValues([]);
            }}
            disabled={loading}
            allowSameValueInOptions
            displayValues={false}
          />
          <AutoSelect
            key={dimensionValuesKey}
            multi={true}
            showValues={false}
            closeOnChange={true}
            values={dimensionValues}
            letClear={true}
            query={() =>
              Promise.resolve(graphData.values.map((item) => ({ text: item.label, value: item.value })))
            }
            handleChange={(values) => {
              setDimensionValues(values);
              forceUpdate();
            }}
            handleDropDownToggle={setDropdownOpen}
            inputStyle={{ position: "absolute" }}
            displayValues={displayValues}
            disabled={loading}
            rootClassName={dimensionValues.length > 0 ? css.rolesPieSectionFilterSelected : ""}
            preOptions={
              <div className={css.preOptions}>
                {dimensionValues?.map(({ value, text }) => (
                  <div key={value} className={css.preOption}>
                    {/* eslint-disable */}
                    <a
                      onClick={() => {
                        setDimensionValues((prev) => prev.filter((item) => item.value !== value));
                        setDropdownOpen(false);
                        forceUpdate();
                      }}
                    >
                      &times;
                    </a>
                    <span>{` ${text}`}</span>
                  </div>
                ))}
              </div>
            }
          />
        </div>
        {Object.keys(graphData.pie).length > 0 ? (
          <EchartsWrapper
            onClick={(e) => {
              setModalData({
                dim: dimension.value,
                value: e.name,
                title: "roles list",
                chartType: "roles_pie"
              });
            }}
            options={pieChartConfig(t("saveImage"), t("accessFrom"), graphData.pie)}
            height={600}
          />
        ) : (
          <PiePreview content={t("noData")} />
        )}
      </div>
    </>
  );
};

export default RolesPieChart;
